'use strict';
/* global jQuery _ */

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Content State Service.
 * Uses Q-based promises for all operations.
 */

(function ($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new ContentStateService instance.
   * @param {string} contentStateServiceUrl - The URL to the content state service in the ACE REST API.
   * @param {Object} serviceSettings - An object containing settings for this service.
   * @param {Object} [serviceSettings.customQ = window.Q] - A Q implementations i.e. angular's $q
   *
   * @typedef {Object} ContentStateService
   * @property {function} updateHiddenState
   * @returns ContentStateService
   **/
  atex.aceui.service.ContentStateService = function (contentStateServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Updates the hidden state of a content.
       *
       * @example
       * ContentStateService.updateHiddenState(
       *  {alias: 'myNamespace/article1', hidden: true, authToken: ...},
       * ).then(function(result) {
       *   ...
       * })
       *
       * @param {string} alias - an alias identifying the content to be updated
       * @param {boolean} hidden - the desired boolean value for the hidden state.
       * @param {string} authToken - An authentication token.
       *
       * @typedef {Object} result - The result of a successfully updated content hidden state.
       * @returns {Promise.<result, atex.aceui.error.Error>}
       */
      updateHiddenState: function ({ alias, hidden, authToken }) {

        if (typeof alias === 'undefined') {
          let e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: 'Missing alias property in requestArgs object',
            origin: 'atex.aceui.service.ContentStateService'
          });

          return q.reject(e);
        }

        if (typeof hidden === 'undefined') {
          let e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: 'Missing hidden property in requestArgs object',
            origin: 'atex.aceui.service.ContentStateService'
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var url = contentStateServiceUrl + '/aceHiddenState'+'?format=json';
        var errorMsg = 'while updating hidden state for content "' + alias + '" to the server';

        $.ajax(url, {
          type: 'POST',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify({ alias, hidden }),

          beforeSend: function (jqXHR) {
            if (authToken) {
              jqXHR.setRequestHeader('X-Auth-Token', authToken);
            }
          },

          success: function (data) {
            deferred.resolve({result: data});
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentStateService', {
              401: 'Could not authenticate user ' + errorMsg,
              403: 'Permission denied ' + errorMsg,
              409: 'Conflict with server version ' + errorMsg,
              'default': 'There was an error ' + errorMsg
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = { alias, hidden, authToken };
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
